import { clientApiFetch } from "@/lib/api/client";

export type RuntimeState = {
  maintenanceActive: boolean;
  title: string | null;
  message: string | null;
  startsAt: string | null;
  endsAt: string | null;
  serverNow: string;
  publicAnnouncement: {
    body: string;
    deepLink: string | null;
    expiresAt: string | null;
    publishAt: string | null;
    severity: AnnouncementSeverity;
    title: string;
  } | null;
};

export type AnnouncementSeverity =
  | "INFO"
  | "WARNING"
  | "CRITICAL"
  | "MAINTENANCE";
export type AnnouncementStatus =
  | "DRAFT"
  | "SCHEDULED"
  | "ACTIVE"
  | "ENDED"
  | "CANCELED";
export type AnnouncementChannel = "NOTIFICATION" | "POPUP" | "PERSISTENT";

export type SystemAnnouncement = {
  id: string;
  title: string;
  body: string;
  severity: AnnouncementSeverity;
  status: AnnouncementStatus;
  channels: AnnouncementChannel[];
  publishAt: string | null;
  expiresAt: string | null;
  toastDurationMs: number | null;
  deepLink: string | null;
  createdAt: string;
  updatedAt: string;
  createdBy?: { id: string; name: string };
};

export type SystemAnnouncementImage = {
  id: string;
  mimeType: string;
  originalFileName: string;
  sizeBytes: number;
  url: string;
  inline: boolean;
};

export type MaintenanceWindow = {
  id: string;
  title: string;
  message: string;
  startsAt: string;
  endsAt: string;
  timezone: string;
  announcementId: string | null;
  blockNonBypass: boolean;
  canceledAt: string | null;
  createdAt: string;
  updatedAt: string;
  announcement?: { channels: AnnouncementChannel[] } | null;
  createdBy?: { id: string; name: string };
};

export function getRuntimeState() {
  return clientApiFetch<RuntimeState>("/api/system/runtime-state", {
    redirectOnUnauthorized: false,
  });
}

export function listPendingAnnouncementPopups() {
  return clientApiFetch<SystemAnnouncement[]>(
    "/api/system/announcements/pending-popup",
    {
      redirectOnUnauthorized: false,
    },
  );
}

export function acknowledgeAnnouncementPopup(id: string) {
  return clientApiFetch(
    `/api/system/announcements/${encodeURIComponent(id)}/acknowledge`,
    {
      body: JSON.stringify({}),
      method: "POST",
      redirectOnUnauthorized: false,
    },
  );
}

export function listAnnouncements() {
  return clientApiFetch<SystemAnnouncement[]>("/api/system/announcements");
}

export function saveAnnouncement(
  input: Partial<SystemAnnouncement> & { attachmentDraftKey?: string },
  id?: string,
) {
  return clientApiFetch<SystemAnnouncement>(
    id
      ? `/api/system/announcements/${encodeURIComponent(id)}`
      : "/api/system/announcements",
    { body: JSON.stringify(input), method: id ? "PATCH" : "POST" },
  );
}

export function cancelAnnouncement(id: string) {
  return clientApiFetch<SystemAnnouncement>(
    `/api/system/announcements/${encodeURIComponent(id)}/cancel`,
    {
      method: "POST",
    },
  );
}

export function uploadAnnouncementImage(input: {
  announcementId?: string;
  draftKey?: string;
  file: File;
}) {
  const body = new FormData();
  body.set("file", input.file);
  body.set("inline", "true");
  if (input.announcementId) body.set("announcementId", input.announcementId);
  if (input.draftKey) body.set("draftKey", input.draftKey);
  return clientApiFetch<SystemAnnouncementImage>(
    "/api/system/announcements/attachments",
    { body, method: "POST" },
  );
}

export function uploadAnnouncementAttachment(input: {
  announcementId?: string;
  draftKey?: string;
  file: File;
}) {
  const body = new FormData();
  body.set("file", input.file);
  if (input.announcementId) body.set("announcementId", input.announcementId);
  if (input.draftKey) body.set("draftKey", input.draftKey);
  return clientApiFetch<SystemAnnouncementImage>(
    "/api/system/announcements/attachments",
    { body, method: "POST" },
  );
}

export function listMaintenanceWindows() {
  return clientApiFetch<MaintenanceWindow[]>("/api/system/maintenance-windows");
}

export function saveMaintenanceWindow(
  input: Partial<MaintenanceWindow>,
  id?: string,
) {
  return clientApiFetch<MaintenanceWindow>(
    id
      ? `/api/system/maintenance-windows/${encodeURIComponent(id)}`
      : "/api/system/maintenance-windows",
    { body: JSON.stringify(input), method: id ? "PATCH" : "POST" },
  );
}

export function cancelMaintenanceWindow(id: string) {
  return clientApiFetch<MaintenanceWindow>(
    `/api/system/maintenance-windows/${encodeURIComponent(id)}/cancel`,
    {
      method: "POST",
    },
  );
}
